/**
 * ChannelPartnerListPage — Issue #345 admin operational table rollout.
 *
 * Renders the channel-partner roster using the shared AdminDataTable so the
 * page picks up advanced search, column visibility, loading/empty/error
 * states and a permission-aware row action menu. Channel partners are
 * customer-facing financial relationships so the destructive action is
 * `停用` (status → `inactive`) via the existing
 * `PUT /api/v1/crm/channel-partners/{id}` endpoint — no physical deletion.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { Eye, PauseCircle, Pencil, PlayCircle } from 'lucide-react';
import {
  useChannelPartnerList,
  useChannelPartnerUpdate,
  CHANNEL_PARTNER_STATUS_LABELS,
  type ChannelPartnerDto,
  type ChannelPartnerStatus,
} from '@/hooks/useCrm';
// Issue #345 audit fix2: useChannelPartnerList 仅接受 `{ page, limit, status }`，
// 之前的 `name` 文本筛选只过滤当前已加载的一页，会让客户在第 N 页搜索时漏掉
// 名字命中但落在其它页的数据。后端补齐渠道合作方名称模糊查询前，工具栏只
// 保留 `status` 下拉这个真正服务端支持的筛选项。
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';
import { Button } from '@/components/ui/button';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';

const DEACTIVATED_STATUS: ChannelPartnerStatus = 'inactive' as ChannelPartnerStatus;
const ACTIVE_STATUS: ChannelPartnerStatus = 'active' as ChannelPartnerStatus;

export default function ChannelPartnerListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, fetch } = useChannelPartnerList();
  const { loading: updating, update } = useChannelPartnerUpdate();
  const [partners, setPartners] = useState<readonly ChannelPartnerDto[]>([]);
  const [total, setTotal] = useState(0);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    status: '',
  });
  const [page, setPage] = useState(1);
  const limit = 20;

  const canRead = hasPermission(permissions, CRM_PERMISSIONS.CHANNEL_PARTNER_READ);
  const canCreate = hasPermission(
    permissions,
    CRM_PERMISSIONS.CHANNEL_PARTNER_CREATE,
  );
  const canUpdate = hasPermission(
    permissions,
    CRM_PERMISSIONS.CHANNEL_PARTNER_UPDATE,
  );

  const statusFilter = (searchValues['status'] ?? '') as
    | ChannelPartnerStatus
    | '';

  const refresh = useCallback(async () => {
    const result = await fetch({
      page,
      limit,
      status: statusFilter !== '' ? statusFilter : undefined,
    });
    if (result) {
      setPartners(result.data);
      setTotal(result.total);
    }
  }, [fetch, page, statusFilter]);

  useEffect(() => {
    if (!canRead) return;
    void refresh();
  }, [canRead, refresh]);

  useEffect(() => {
    if (error) toast.error(`加载渠道合作方列表失败：${error.message}`);
  }, [error]);

  // Issue #345 audit fix2: 服务端翻页 + 真实 total，不再叠加纯前端的 name 子串
  // 过滤，避免命中数与翻页 total 不一致。
  const visibleRows = partners;

  const columns: ReadonlyArray<AdminDataTableColumn<ChannelPartnerDto>> = useMemo(
    () => [
      {
        key: 'name',
        header: '名称',
        alwaysVisible: true,
        render: (p) => <span className="font-medium">{p.name}</span>,
      },
      {
        key: 'status',
        header: '状态',
        render: (p) => (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
            {CHANNEL_PARTNER_STATUS_LABELS[p.status]}
          </span>
        ),
      },
      {
        key: 'commissionRate',
        header: '佣金率',
        render: (p) => p.commissionRate ?? '—',
      },
      {
        key: 'createdAt',
        header: '创建时间',
        render: (p) => new Date(p.createdAt).toLocaleDateString('zh-CN'),
      },
    ],
    [],
  );

  const transitionStatus = useCallback(
    async (p: ChannelPartnerDto, nextStatus: ChannelPartnerStatus, verb: string) => {
      const confirmed = window.confirm(
        `将渠道合作方「${p.name}」${verb}。该操作不会物理删除佣金/合作历史，可后续重新启用。`,
      );
      if (!confirmed) return;
      const next = await update(p.id, {
        expectedVersion: p.version,
        status: nextStatus,
      });
      if (next) {
        toast.success(`渠道合作方「${p.name}」已${verb}`);
        await refresh();
      }
    },
    [refresh, update],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<ChannelPartnerDto>> = useMemo(
    () => [
      {
        key: 'view',
        label: '查看',
        icon: <Eye className="h-3.5 w-3.5" />,
        onSelect: (p) => navigate(`/crm/channel-partners/${p.id}`),
      },
      {
        key: 'edit',
        label: '编辑',
        icon: <Pencil className="h-3.5 w-3.5" />,
        visible: () => canUpdate,
        onSelect: (p) => navigate(`/crm/channel-partners/${p.id}`),
      },
      {
        key: 'reactivate',
        label: '重新启用',
        icon: <PlayCircle className="h-3.5 w-3.5" />,
        visible: (p) => canUpdate && p.status !== ACTIVE_STATUS,
        disabled: () => updating,
        onSelect: (p) => void transitionStatus(p, ACTIVE_STATUS, '重新启用'),
      },
      {
        key: 'deactivate',
        label: '停用（归档）',
        icon: <PauseCircle className="h-3.5 w-3.5" />,
        destructive: true,
        visible: (p) => canUpdate && p.status !== DEACTIVATED_STATUS,
        disabled: () => updating,
        onSelect: (p) => void transitionStatus(p, DEACTIVATED_STATUS, '停用'),
      },
    ],
    [canUpdate, navigate, transitionStatus, updating],
  );

  if (!canRead) return <AccessDenied resource="渠道合作方" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">渠道合作方</h2>
        {canCreate && (
          <Button
            type="button"
            onClick={() => navigate('/crm/channel-partners/create')}
          >
            新建合作方
          </Button>
        )}
      </div>

      <AdminDataTable<ChannelPartnerDto>
        testIdPrefix="crm-channel-partners"
        columns={columns}
        rows={visibleRows}
        isLoading={loading}
        error={error}
        onRetry={refresh}
        emptyMessage="暂无渠道合作方数据"
        filters={{
          fields: [
            {
              kind: 'select',
              name: 'status',
              label: '状态',
              options: (
                Object.keys(CHANNEL_PARTNER_STATUS_LABELS) as ChannelPartnerStatus[]
              ).map((s) => ({
                value: s,
                label: CHANNEL_PARTNER_STATUS_LABELS[s],
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ status: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize: limit,
          total,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />
    </div>
  );
}
